import {
  CLINICAL_BRANCHING,
  COACHING_STYLE,
  DIABETES_TESTIMONIAL,
  DIABETIC_TYPE_1,
  DIABETIC_TYPE_2,
  ELIGIBILITY_BRANCHING,
  GALLBLADDER_BRANCH_END,
  GUIDED_SELLING_QUESTION2,
  HIDE_NEXT_IF_BMI_LESS_27,
  NEXT_WORKSHOP_INFO_DIABETES,
  NEXT_WORKSHOP_INFO_POINTS,
  PANCREATITIS_GALLBLADDER,
  PAST_DIAGNOSIS,
  PAST_DIAGNOSIS2,
  PAST_ED_DIAGNOSIS,
  PREGNANCY,
  PRODUCT_BRANCHING,
  SHOW_IF_BMI_GREATER_27,
  SHOW_NEXT_IF_BMI_GREATER_27,
  SHOW_NEXT_IF_BMI_LESS_27,
  SKIP_ANSWER,
  START_CLINICAL_BRANCH,
  START_DIABETES_BRANCH,
  START_DIABETES_WORKSHOP_BRANCH,
  START_POINTS_BRANCH,
  START_POINTS_WORKSHOP_BRANCH,
  START_SHORT_BEHAVIORAL_BRANCH,
  WORKSHOP_INFO_DIABETES,
  WORKSHOP_INFO_POINTS
} from "config/tags";
import {
  isStateSupported,
  isSuicidal,
  mainIsEligibleSequence
} from "utils/eligibility";
import { getCoachPreferenceUrlPath } from "./coachRouting";
import { getResultsPageUniV4 } from "utils/resultFunctions/unifiedV4";
import { CLINICAL_PROGRAM } from "utils/resultFunctions/constants";
import {
  findAnswerByQuestionTag,
  findAnswerByTagAndByQuestionTag
} from "utils/questions";

export const uniRouting = ({
  nextUrlPath: nextUrlPathInit,
  question,
  answers,
  assessment,
  routes,
  profile
}) => {
  let nextUrlPath = nextUrlPathInit;

  // Routing for showing pancreatitis and gallbladder questions.
  if (question.tags.includes(PANCREATITIS_GALLBLADDER)) {
    const diabetesAnswer = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_1,
      PANCREATITIS_GALLBLADDER,
      answers,
      assessment.questions
    );

    if (diabetesAnswer) {
      nextUrlPath = routes.question(
        assessment.questions.find(q => q._id === diabetesAnswer.nextQuestionId)
          .urlPath
      );
    }
  }

  // Routing for the end of gallbladder branch.
  if (question.tags.includes(GALLBLADDER_BRANCH_END)) {
    const diabetesAnswer = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_1,
      PANCREATITIS_GALLBLADDER,
      answers,
      assessment.questions
    );

    if (diabetesAnswer) {
      nextUrlPath = routes.question(
        assessment.questions.find(q => q._id === diabetesAnswer.nextQuestionId)
          .urlPath
      );
    }
  }

  // Routing for diabetes testimonial question.
  if (question.tags.includes(DIABETES_TESTIMONIAL)) {
    const diabetes2Question = assessment.questions.find(q =>
      q.tags.includes(PAST_DIAGNOSIS2)
    );
    const diabetes2Answer = answers.find(
      a => a.questionId === diabetes2Question._id
    );

    if (!diabetes2Answer) {
      nextUrlPath = routes.question(diabetes2Question.urlPath);
    }
  }

  if (question.tags.includes(PRODUCT_BRANCHING)) {
    // Answer to product branching question.
    // Currently: Are you interested in medication?
    const productBranchingAnswer = findAnswerByQuestionTag(
      PRODUCT_BRANCHING,
      answers,
      assessment.questions
    );
    const isEligible =
      mainIsEligibleSequence(answers, profile, assessment.questions) &&
      isStateSupported(profile);

    if (productBranchingAnswer === "Yes" && isEligible) {
      const clinicalBranchQuestion = assessment.questions.find(q =>
        q.tags.includes(START_CLINICAL_BRANCH)
      );
      nextUrlPath = routes.question(clinicalBranchQuestion.urlPath);
    }
  }

  return nextUrlPath;
};

export const uni1Routing = ({
  nextUrlPath: nextUrlPathInit,
  question,
  answers,
  assessment,
  routes,
  newQuiz
}) => {
  let nextUrlPath = nextUrlPathInit;

  // Uni1: Routing to diabetes testimonial question from Diabetes2 question.
  if (question.tags.includes(PAST_DIAGNOSIS2)) {
    const diabetesAnswer = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_2,
      PAST_DIAGNOSIS2,
      answers,
      assessment.questions
    );

    const diabetesTestimonialQuestion = assessment.questions.find(q =>
      q.tags.includes(DIABETES_TESTIMONIAL)
    );
    const diabetesTestimonialAnswer = answers.find(
      a => a.questionId === diabetesTestimonialQuestion._id
    );

    // If user has diabetes type 2
    if (diabetesAnswer) {
      // and was not showed diabetes testimonial page yet.
      if (!diabetesTestimonialAnswer) {
        nextUrlPath = routes.question(diabetesTestimonialQuestion.urlPath);
      } else {
        // already saw diabetes testimonial page, so go to the next question.
        const anyNextQuestionExceptDiabetesTestimonial = assessment.questions
          .find(q => q.tags.includes(PAST_DIAGNOSIS2))
          .answers.presentationMetadata.find(
            a => !a.tags.includes(DIABETIC_TYPE_2)
          ).nextQuestionId;

        nextUrlPath = routes.question(
          assessment.questions.find(
            q => q._id === anyNextQuestionExceptDiabetesTestimonial
          ).urlPath
        );
      }
    }
  }

  // Deciding Points or Diabetes branch.
  const coachPreferenceUrlPath = getCoachPreferenceUrlPath({
    question,
    assessment,
    answers,
    routes,
    newQuiz
  });
  const isOnCoreRecommendedQuestion =
    question.tags.includes(GUIDED_SELLING_QUESTION2) && !coachPreferenceUrlPath;
  const isOnPremiumRecommendedQuestion = question.tags.includes(COACHING_STYLE);

  if (isOnCoreRecommendedQuestion || isOnPremiumRecommendedQuestion) {
    // Decide whether to recommend Points or Diabetes program.
    const diabetesType1AnswerObj = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_1,
      PAST_DIAGNOSIS,
      answers,
      assessment.questions
    );

    const diabetesType2AnswerObj = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_2,
      PAST_DIAGNOSIS2,
      answers,
      assessment.questions
    );

    if (diabetesType1AnswerObj || diabetesType2AnswerObj) {
      const diabetesBranchQuestion = assessment.questions.find(q =>
        q.tags.includes(
          isOnCoreRecommendedQuestion
            ? START_DIABETES_BRANCH
            : START_DIABETES_WORKSHOP_BRANCH
        )
      );

      // Diabetes program
      nextUrlPath = routes.question(diabetesBranchQuestion.urlPath);
    } else {
      const pointsBranchQuestion = assessment.questions.find(q =>
        q.tags.includes(
          isOnCoreRecommendedQuestion
            ? START_POINTS_BRANCH
            : START_POINTS_WORKSHOP_BRANCH
        )
      );

      // Points program
      nextUrlPath = routes.question(pointsBranchQuestion.urlPath);
    }
  }

  // Show Workshop info page if user was recommended Premium.
  const isOnNextIsWorkshopInfoPointsQuestion = question.tags.includes(
    NEXT_WORKSHOP_INFO_POINTS
  );
  const isOnNextIsWorkshopInfoDiabetesQuestion = question.tags.includes(
    NEXT_WORKSHOP_INFO_DIABETES
  );
  const coachingPrefQuestionIds = assessment.questions
    .filter(q => q.tags.includes(COACHING_STYLE))
    .map(q => q._id);
  const coachingPrefAnswer = answers.find(a =>
    coachingPrefQuestionIds.includes(a.questionId)
  );

  if (
    (isOnNextIsWorkshopInfoPointsQuestion ||
      isOnNextIsWorkshopInfoDiabetesQuestion) &&
    coachingPrefAnswer
  ) {
    const workshopInfoPage = assessment.questions.find(q =>
      q.tags.includes(
        isOnNextIsWorkshopInfoPointsQuestion
          ? WORKSHOP_INFO_POINTS
          : WORKSHOP_INFO_DIABETES
      )
    );

    nextUrlPath = routes.question(workshopInfoPage.urlPath);
  }

  return nextUrlPath;
};

export const uni2Routing = ({
  nextUrlPath: nextUrlPathInit,
  question,
  answers,
  assessment,
  routes,
  profile
}) => {
  let nextUrlPath = nextUrlPathInit;

  // Uni2: Routing to diabetes testimonial question from Diabetes2 or Diabetes1 question.
  if (question.tags.includes(PAST_DIAGNOSIS2)) {
    const diabetes2Answer = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_2,
      PAST_DIAGNOSIS2,
      answers,
      assessment.questions
    );
    const diabetes1Answer = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_1,
      PAST_DIAGNOSIS,
      answers,
      assessment.questions
    );

    const diabetesTestimonialQuestion = assessment.questions.find(q =>
      q.tags.includes(DIABETES_TESTIMONIAL)
    );
    const diabetesTestimonialAnswer = answers.find(
      a => a.questionId === diabetesTestimonialQuestion._id
    );

    // If user has diabetes type 1 or 2
    if (diabetes1Answer || diabetes2Answer) {
      // and was not showed diabetes testimonial page yet.
      if (!diabetesTestimonialAnswer) {
        nextUrlPath = routes.question(diabetesTestimonialQuestion.urlPath);
      } else {
        // already saw diabetes testimonial page, so go to bulimia question.
        nextUrlPath = routes.question(
          assessment.questions.find(q => q.tags.includes(PAST_ED_DIAGNOSIS))
            .urlPath
        );
      }
    }
  }

  // Skip answer on Clinical branch start for Uni2.
  if (question.tags.includes(START_CLINICAL_BRANCH)) {
    const skipAnswerObj = findAnswerByTagAndByQuestionTag(
      SKIP_ANSWER,
      START_CLINICAL_BRANCH,
      answers,
      assessment.questions
    );

    if (skipAnswerObj) {
      const diabetesType1AnswerObj = findAnswerByTagAndByQuestionTag(
        DIABETIC_TYPE_1,
        PAST_DIAGNOSIS,
        answers,
        assessment.questions
      );

      const diabetesType2AnswerObj = findAnswerByTagAndByQuestionTag(
        DIABETIC_TYPE_2,
        PAST_DIAGNOSIS2,
        answers,
        assessment.questions
      );

      if (diabetesType1AnswerObj || diabetesType2AnswerObj) {
        nextUrlPath = routes.question(
          assessment.questions.find(q => q.tags.includes(START_DIABETES_BRANCH))
            .urlPath
        );
      }
    }
  }

  if (question.tags.includes(PRODUCT_BRANCHING)) {
    // Answer to product branching question.
    // Currently: Are you interested in medication?
    const productBranchingAnswer = findAnswerByQuestionTag(
      PRODUCT_BRANCHING,
      answers,
      assessment.questions
    );

    const diabetesType1AnswerObj = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_1,
      PAST_DIAGNOSIS,
      answers,
      assessment.questions
    );

    const diabetesType2AnswerObj = findAnswerByTagAndByQuestionTag(
      DIABETIC_TYPE_2,
      PAST_DIAGNOSIS2,
      answers,
      assessment.questions
    );

    const isEligible =
      mainIsEligibleSequence(answers, profile, assessment.questions) &&
      isStateSupported(profile);
    const ineligibleAndNotTakingGLP =
      !isEligible &&
      (productBranchingAnswer === "Yes" || productBranchingAnswer === "No");
    const notInterestedInMedsAndDiabetesType2 =
      diabetesType2AnswerObj && productBranchingAnswer === "No";

    if (ineligibleAndNotTakingGLP || notInterestedInMedsAndDiabetesType2) {
      if (diabetesType1AnswerObj || diabetesType2AnswerObj) {
        const diabetesBranchQuestion = assessment.questions.find(q =>
          q.tags.includes(START_DIABETES_BRANCH)
        );

        nextUrlPath = routes.question(diabetesBranchQuestion.urlPath);
      }
    }
  }

  return nextUrlPath;
};

export const uni3Routing = ({
  nextUrlPath: nextUrlPathInit,
  question,
  answers,
  assessment,
  routes,
  profile
}) => {
  let nextUrlPath = nextUrlPathInit;

  if (question.tags.includes(ELIGIBILITY_BRANCHING)) {
    const isEligible =
      mainIsEligibleSequence(answers, profile, assessment.questions) &&
      !isSuicidal(answers, assessment.questions);

    if (!isEligible) {
      const isPregnant =
        findAnswerByQuestionTag(
          PREGNANCY,
          answers,
          assessment.questions
        )?.toLowerCase() === "yes";
      const hasEatingDisorder =
        findAnswerByQuestionTag(
          PAST_ED_DIAGNOSIS,
          answers,
          assessment.questions
        )?.toLowerCase() === "yes";

      if (!isPregnant && !hasEatingDisorder) {
        const shortBehavioralBranchQuestion = assessment.questions.find(q =>
          q.tags.includes(START_SHORT_BEHAVIORAL_BRANCH)
        );

        nextUrlPath = routes.question(shortBehavioralBranchQuestion.urlPath);
      }
    }
  }

  if (question.tags.includes(HIDE_NEXT_IF_BMI_LESS_27)) {
    if (profile.bmi < 27) {
      const questionLess27Bmi = assessment.questions.find(q =>
        q.tags.includes(SHOW_NEXT_IF_BMI_LESS_27)
      );
      nextUrlPath = routes.question(questionLess27Bmi.urlPath);
    }
  }

  return nextUrlPath;
};

export const uni4Routing = ({
  nextUrlPath: nextUrlPathInit,
  question,
  answers,
  assessment,
  routes,
  profile
}) => {
  let nextUrlPath = nextUrlPathInit;

  if (question.tags.includes(CLINICAL_BRANCHING)) {
    const currentResults = getResultsPageUniV4(
      answers,
      assessment.questions,
      profile
    );

    if (currentResults === CLINICAL_PROGRAM) {
      const startClinicalBranchQuestion = assessment.questions.find(q =>
        q.tags.includes(START_CLINICAL_BRANCH)
      );

      nextUrlPath = routes.question(startClinicalBranchQuestion.urlPath);
    }
  }

  if (question.tags.includes(SHOW_NEXT_IF_BMI_GREATER_27)) {
    if (profile.bmi >= 27) {
      const bmiShow = assessment.questions.find(q =>
        q.tags.includes(SHOW_IF_BMI_GREATER_27)
      );

      if (nextUrlPath === routes.results) {
        nextUrlPath = routes.question(bmiShow.urlPath);
      }
    }
  }

  return nextUrlPath;
};
